/**
 * Shared CSV export helpers for unit-bearing FieldFormats.
 *
 * CSV columns need one unit for the whole column, not per-row auto-scaling. The
 * on-screen display can happily render "12 kV" one row and "500 mV" the next
 * because a human reads them in context, but in a spreadsheet that mixes
 * magnitudes in a single column and makes the data unusable.
 *
 * These helpers pick a stable column-wide unit for a given FieldFormat and
 * return the bare number that matches that unit:
 *
 *   - `csvUnitFor(format)`   → the unit string to show in the column header,
 *     or null when the format has no meaningful unit (text, number, date, …).
 *   - `csvValueFor(raw, format)` → the bare-number string for a cell in that
 *     column. Returns null for non-numeric formats (caller falls back to the
 *     regular display formatter), and "" when the display would be blank
 *     (hideWhenNull / hideWhenZero / null value).
 *
 * Consumers:
 *   - `apps/dashboard/components/Explorer/tables/deviceFieldExport.ts` (Explore CSV)
 *   - `packages/edges/src/utils/chartExport.ts` (chart CSV export)
 */

import { formatFieldValue } from "./formatFieldValue";
import type { FieldFormat, FieldValue } from "./types";

/** Base unit substituted for auto-scaled formats when no explicit `unit` is set. */
const DEFAULT_BASE_UNIT: Record<string, string> = {
  power: "W",
  energy: "Wh",
  voltage: "V",
  current: "A",
  resistance: "Ω",
  distance: "m",
};

/** Numeric format types that support bare-number CSV export. */
const NUMERIC_FORMAT_TYPES = new Set([
  "number",
  "power",
  "energy",
  "voltage",
  "current",
  "resistance",
  "temperature",
  "distance",
  "percentage",
]);

/**
 * Pick the CSV column unit for a FieldFormat.
 *
 * Rules:
 * - Explicit `unit` on the format wins.
 * - Otherwise fall back to the format's canonical base unit
 *   (`DEFAULT_BASE_UNIT` lookup) so every row in the column shares one unit
 *   even if the on-screen display would auto-scale.
 * - Energy honours `sourceUnit` (e.g. meter consumption is stored in kWh) as
 *   the natural stable unit when neither `unit` nor a base fallback would fit.
 * - Temperature symbols are normalised from `"C"`/`"CELSIUS"` etc. to `"°C"`.
 * - Formats with no meaningful unit (text, number, date, boolean, currency,
 *   phone, custom) return null.
 */
export function csvUnitFor(format: FieldFormat | undefined | null): string | null {
  if (!format) return null;
  switch (format.type) {
    case "percentage":
      return "%";
    case "power":
      return format.unit ?? DEFAULT_BASE_UNIT.power;
    case "energy":
      return format.unit ?? format.sourceUnit ?? DEFAULT_BASE_UNIT.energy;
    case "voltage":
      return format.unit ?? DEFAULT_BASE_UNIT.voltage;
    case "current":
      return format.unit ?? DEFAULT_BASE_UNIT.current;
    case "resistance":
      return format.unit ?? DEFAULT_BASE_UNIT.resistance;
    case "temperature": {
      // Default to °F when no unit is set — that mirrors `formatTemperature`,
      // which also falls back to Fahrenheit. Returning null here would drop
      // the header unit AND route csvValueFor through the raw-`String(num)`
      // path, letting unrounded raw values leak into cells that the UI
      // rounds via `format.decimals`.
      const u = typeof format.unit === "string" ? format.unit : "F";
      if (u === "C" || u === "CELSIUS") return "°C";
      if (u === "F" || u === "FAHRENHEIT") return "°F";
      if (u === "K" || u === "KELVIN") return "K";
      return u;
    }
    case "distance":
      return format.unit ?? DEFAULT_BASE_UNIT.distance;
    default:
      return null;
  }
}

/**
 * Convert a raw value to a bare-number CSV string in the same unit
 * `csvUnitFor` reports.
 *
 * Returns:
 *  - `null` when the format is non-numeric (caller falls back to the display
 *    formatter, e.g. text/date/enum).
 *  - `""` when the display would suppress the value (hideWhenNull / hideWhenZero).
 *  - a bare number string otherwise, with the trailing unit stripped so the
 *    cell can be summed / charted in the spreadsheet.
 */
export function csvValueFor(raw: unknown, format: FieldFormat): string | null {
  if (!NUMERIC_FORMAT_TYPES.has(format.type)) return null;

  // Normalise numeric strings — some adapters serialise numbers as strings.
  let numValue: number;
  if (typeof raw === "number") {
    numValue = raw;
  } else if (typeof raw === "string") {
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) return null;
    numValue = parsed;
  } else {
    return null;
  }

  const unit = csvUnitFor(format);

  if (unit !== null) {
    // Force the CSV cell into the same fixed unit shown in the header.
    //
    // We ALWAYS force `autoScale: false` when we know the CSV column unit,
    // even when the caller supplied an explicit `unit` on the format. Reason:
    // `formatDistance` (and defensively anything else that adopts the same
    // pattern) ignores `unit` when `autoScale` is truthy, so a format like
    // `{ type: "distance", unit: "m", autoScale: true }` would render "1.5 km"
    // while the header says (m). The trailing-"m" strip below would then
    // silently produce "1.5 k" in the cell. Disabling autoScale on the format
    // copy forces the formatter to honour the unit we picked.
    //
    // biome-ignore lint/suspicious/noExplicitAny: safe format extension for CSV path
    let csvFormat: any;
    if (format.type === "percentage") {
      // Percentage has no `unit` / `autoScale` fields — pass through as-is.
      csvFormat = format;
    } else if (format.type === "energy") {
      // Auto-scaled energy → substitute sourceUnit (kWh) or base Wh so the
      // formatter produces values in the header's column unit.
      const explicit = (format as { unit?: string }).unit;
      csvFormat = explicit
        ? { ...format, autoScale: false }
        : { ...format, unit: (format as { sourceUnit?: string }).sourceUnit ?? "Wh", autoScale: false };
    } else if (format.type === "temperature") {
      // Temperature is special: `formatTemperature` accepts either a symbol
      // ("C"/"F"/"K") or a string ("CELSIUS"/...), not the display glyph
      // "°F". Preserve the explicit unit when set; otherwise leave `unit`
      // undefined so formatTemperature falls back to its own "F" default.
      csvFormat = { ...format };
    } else {
      // Unit-bearing formats (power / voltage / current / resistance /
      // distance): substitute the base unit when auto-scaled and always
      // force autoScale off.
      const explicit = (format as { unit?: string }).unit;
      csvFormat = { ...format, unit: explicit ?? unit, autoScale: false };
    }

    const formatted = formatFieldValue(numValue as FieldValue, csvFormat);
    if (!formatted) return "";
    if (typeof formatted === "string" && formatted.startsWith("device.")) {
      // Enum translation key — not a numeric output; treat as non-numeric.
      return null;
    }
    const s = String(formatted);
    if (s.endsWith(` ${unit}`)) return s.slice(0, -(unit.length + 1));
    if (s.endsWith(unit)) return s.slice(0, -unit.length);
    return s;
  }

  // Non-unit numeric formats (currently just "number"). Route through
  // formatFieldValue so decimals / hideWhenZero / etc. still apply.
  if (format.type === "number") {
    const formatted = formatFieldValue(numValue as FieldValue, format);
    if (!formatted) return "";
    return String(formatted);
  }
  return String(numValue);
}
